import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import GeoContext from 'contexts/geoContext'
import type { EnrichmentStatus, ItineraryUpdate, EnrichmentCompleteEvent, TripItineraryData } from 'tripPlanner/api/types'
import { applyItineraryUpdates, applyEnrichmentUpdate } from 'tripPlanner/utils/itineraryUpdates'
import { orderTripItineraryStops } from 'tripPlanner/utils/itineraryStopOrder'
import { patchExperienceUpdatesWithPendingDaySelection } from 'tripPlanner/utils/pendingExperienceDaySelection'
import { useGetItinerary } from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { computeTripDatesFromStops } from 'tripPlanner/utils/chatDates'
import {
  showTripActionConfirmedSnackbar,
  showTripDatesConfirmedSnackbar,
  shouldConfirmTripAction,
} from 'tripPlanner/components/Snackbar/tripActionSnackbar'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { AddedItineraryItemLink } from 'tripPlanner/assistant/contexts/AddedItineraryItemContext'
import type { ItineraryScrollTarget } from 'tripPlanner/assistant/contexts/ItineraryContext'

export type { ItineraryScrollTarget } from 'tripPlanner/assistant/contexts/ItineraryContext'

interface UseItineraryUpdatesArgs {
  chatId: number | undefined
  // True while the route names a chat that hasn't been copied into chatId yet, which leaves
  // the itinerary query disabled. Folded into isItineraryLoading so the first render after a
  // refresh reads as "loading", not "empty".
  isChatIdSyncPending: boolean
  // Ref to track streaming state - prevents refetchOnWindowFocus from overwriting optimistic updates mid-stream
  isStreamingRef: React.RefObject<boolean>
  // Ref to the assistant turn currently streaming, so an add can be attributed to the message
  // that caused it. A ref (not a value) because this hook runs before the chat core.
  activeAssistantMessageIdRef: React.RefObject<string | undefined>
}

interface UseItineraryUpdatesReturn {
  itineraryData: TripItineraryData | undefined
  isItineraryLoading: boolean
  enrichingStopIds: Set<number>
  enrichingItemIds: Set<number>
  scrollTarget: ItineraryScrollTarget | undefined
  clearScrollTarget: () => void
  addedItemLink: AddedItineraryItemLink | undefined
  // Streaming callbacks receive chatId as first argument to avoid stale closure issues
  handleItineraryUpdates: (chatIdArg: number, updates: Array<ItineraryUpdate>) => void
  handleEnrichmentComplete: (chatIdArg: number, event: EnrichmentCompleteEvent) => void
  handleRefetchItinerary: (chatIdArg: number) => void
  // Non-streaming callbacks use closure chatId (safe for user-initiated actions on current chat)
  refetchCurrentChatItinerary: () => void
}

/**
 * Resolves where a surface should scroll for an update. stopVisits is authoritative;
 * the flat stopId/dayId pair is the deprecated fallback still sent by older streams.
 */
function resolveScrollTarget(update: ItineraryUpdate): ItineraryScrollTarget | undefined {
  const itemId = 'itemId' in update ? update.itemId : undefined

  if ('stopVisits' in update && update.stopVisits?.length > 0) {
    return { stopId: update.stopVisits[0].tripStopId, dayId: update.stopVisits[0].startDayId, itemId }
  }
  if ('stopId' in update && update.stopId !== undefined) {
    return { stopId: update.stopId, dayId: 'dayId' in update ? update.dayId : undefined, itemId }
  }
  return undefined
}

// ── Enrichment derivation helpers ─────────────────────────────────────

const ENRICHING_STATUSES: ReadonlySet<EnrichmentStatus> = new Set(['pending', 'enriching'])

function isEnriching(entity: { enrichmentStatus?: EnrichmentStatus }): boolean {
  return entity.enrichmentStatus !== undefined && ENRICHING_STATUSES.has(entity.enrichmentStatus)
}

function collectEnrichingIds(
  entities: ReadonlyArray<{ id: number; enrichmentStatus?: EnrichmentStatus }>,
  ids: Set<number>,
): void {
  for (const entity of entities) {
    if (isEnriching(entity)) ids.add(entity.id)
  }
}

/**
 * Returns a referentially-stable Set that only changes identity when its contents change.
 * Prevents unnecessary re-renders of ItineraryContext consumers and refetchInterval churn
 * when itineraryData reference changes but the derived enriching IDs remain the same.
 */
function useStableSet(rawSet: Set<number>): Set<number> {
  const ref = useRef(rawSet)

  if (!setsAreEqual(rawSet, ref.current)) {
    ref.current = rawSet
  }

  return ref.current
}

function setsAreEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

// ── Hook ──────────────────────────────────────────────────────────────

/**
 * Manages itinerary state for the chat trip planner.
 *
 * Data Flow:
 * 1. React Query (`useGetItinerary`) is the single source of truth for itinerary data
 * 2. During streaming, optimistic updates are applied via `queryClient.setQueryData`
 * 3. After stream completes, `handleRefetchItinerary` invalidates the cache to sync with server
 * 4. `refetchOnWindowFocus` is disabled during streaming to prevent overwriting optimistic updates
 *    - When not streaming, window focus triggers refetch to ensure fresh data
 *
 * Enrichment State (derived, not managed):
 * - `enrichingStopIds` / `enrichingItemIds` are derived from `enrichmentStatus` on itinerary entities
 * - Self-terminating: enrichment finishes → status becomes terminal → Sets empty → refetchInterval stops
 * - Self-resetting: chat switch → itinerary data changes → Sets reflect new chat automatically
 * - `refetchInterval` (3s) acts as a fallback when the stream closes before all enrichment completes
 *   (e.g., 15s BE timeout exceeded). Stream events handle the happy path via optimistic cache patches.
 */
export function useItineraryUpdates({
  chatId,
  isChatIdSyncPending,
  isStreamingRef,
  activeAssistantMessageIdRef,
}: UseItineraryUpdatesArgs): UseItineraryUpdatesReturn {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const eventBus = useTripPlannerEventBus()
  const { currentRegionCode } = useContext(GeoContext)

  // refetchInterval is driven by derived enrichment state. useState + sync-during-render
  // ensures the interval is current on the same render cycle that detects enriching entities,
  // avoiding the one-render lag that a ref-based approach would have.
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { data: itineraryData, isInitialLoading } = useGetItinerary(chatId, currentRegionCode, {
    refetchOnWindowFocus: () => !isStreamingRef.current,
    refetchInterval,
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false
      return error !== null && typeof error === 'object' && 'status' in error && error.status === 403
    },
    retryDelay: 1000,
  })

  // react-query reports isInitialLoading as `isLoading && isFetching`, and a disabled query
  // isn't fetching — so the render before chatId lands would otherwise read as "settled with
  // no itinerary" and flash the empty state. A new chat has no route chat id, so it stays
  // false there and the empty state still shows immediately, as it should.
  const isItineraryLoading = isInitialLoading || isChatIdSyncPending

  const orderedItineraryData = useMemo(
    () => orderTripItineraryStops(itineraryData),
    [itineraryData],
  )

  // ── Derive enriching entity Sets from itinerary data ──
  // Single pass: collects both stop and item IDs in one iteration.
  // Only 'pending' and 'enriching' are active statuses. undefined = doesn't need enrichment.

  const { stopIds: enrichingStopIdsRaw, itemIds: enrichingItemIdsRaw } = useMemo(() => {
    const stopIds = new Set<number>()
    const itemIds = new Set<number>()
    for (const stop of orderedItineraryData?.stops ?? []) {
      if (isEnriching(stop)) stopIds.add(stop.id)
      for (const day of stop.days) {
        collectEnrichingIds(day.items, itemIds)
      }
      collectEnrichingIds(stop.itemsWithoutDay ?? [], itemIds)
    }
    return { stopIds, itemIds }
  }, [orderedItineraryData])

  const enrichingStopIds = useStableSet(enrichingStopIdsRaw)
  const enrichingItemIds = useStableSet(enrichingItemIdsRaw)

  // Sync refetchInterval with derived enrichment state (setState-during-render pattern).
  // React immediately re-renders before commit, so useGetItinerary gets the correct interval.
  const nextInterval: number | false = (enrichingStopIds.size > 0 || enrichingItemIds.size > 0) ? 3000 : false
  if (nextInterval !== refetchInterval) {
    setRefetchInterval(nextInterval)
  }

  const [scrollTarget, setScrollTarget] = useState<ItineraryScrollTarget | undefined>(undefined)
  const [addedItemLink, setAddedItemLink] = useState<AddedItineraryItemLink | undefined>(undefined)
  const stopOnlyAddChatIdRef = useRef<number | undefined>(undefined)
  const itemAddedChatIdRef = useRef<number | undefined>(undefined)

  const clearScrollTarget = useCallback(() => {
    setScrollTarget(undefined)
  }, [])

  // The auto-scroll on add only lands if a Timeline is already mounted. Re-arm the target
  // when View is used, so the surface that just switched views scrolls once it renders.
  useEventSubscription('view_itinerary_item', (event) => {
    setScrollTarget({ stopId: event.stopId, dayId: event.dayId, itemId: event.itemId })
  })

  useEventSubscription('reset_chat_state', () => {
    stopOnlyAddChatIdRef.current = undefined
    itemAddedChatIdRef.current = undefined
  })

  // chatIdArg is passed from streaming code to avoid stale closure (streaming has authoritative chatId)
  const handleItineraryUpdates = useCallback((chatIdArg: number, updates: Array<ItineraryUpdate>) => {
    if (updates.length === 0) return

    const prevData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
    )
    const prevDates = computeTripDatesFromStops(prevData?.stops)
    const normalizedUpdates = patchExperienceUpdatesWithPendingDaySelection(prevData, updates)

    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
        (prev) => applyItineraryUpdates(prev, normalizedUpdates),
      )
    } catch {
      // Optimistic update failed, will be corrected by server response
    }

    const newData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
    )
    const newDates = computeTripDatesFromStops(newData?.stops)

    const tripDatesChanged = prevDates?.startDate !== newDates?.startDate || prevDates?.endDate !== newDates?.endDate

    const addedStops = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_added' }> => u.kind === 'stop_added')
    const addedItems = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_added' }> => u.kind === 'item_added')

    // The in-chat link targets the first added item. Attribute it to the assistant
    // message that produced the add so it stays anchored to that turn.
    const firstAddedTarget = addedItems.length > 0 ? resolveScrollTarget(addedItems[0]) : undefined
    const addingMessageId = activeAssistantMessageIdRef.current
    if (firstAddedTarget && addingMessageId) {
      setAddedItemLink({ messageId: addingMessageId, ...firstAddedTarget })
    }

    if (addedItems.length > 0) {
      itemAddedChatIdRef.current = chatIdArg
      stopOnlyAddChatIdRef.current = undefined
      eventBus.dispatch({ type: 'itinerary_item_added' })
    } else if (addedStops.length > 0 && itemAddedChatIdRef.current !== chatIdArg) {
      // A later batch may still add an item. Close the drawer at turn end only if it does not.
      stopOnlyAddChatIdRef.current = chatIdArg
    }

    const addedViewTarget = firstAddedTarget ?? (addedStops[0] && resolveScrollTarget(addedStops[0]))

    if (addedItems.length > 0 || addedStops.length > 0) {
      if (addedViewTarget) {
        setScrollTarget(addedViewTarget)
      }

      if (shouldConfirmTripAction('add')) {
        showTripActionConfirmedSnackbar(intl, 'add', addedViewTarget && (
          () => eventBus.dispatch({ type: 'view_itinerary_item', ...addedViewTarget })
        ))
      }
      return
    }

    // An add in the same batch takes priority over an update.
    const updatedStops = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_updated' }> => u.kind === 'stop_updated')
    const updatedItems = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_updated' }> => u.kind === 'item_updated')

    if (updatedItems.length > 0 || updatedStops.length > 0) {
      let updatedViewTarget: ItineraryScrollTarget | undefined
      if (updatedItems.length > 0) {
        updatedViewTarget = resolveScrollTarget(updatedItems[0])
      } else if (updatedStops.length > 0) {
        updatedViewTarget = resolveScrollTarget(updatedStops[0])
      }

      if (updatedViewTarget) setScrollTarget(updatedViewTarget)
      if (shouldConfirmTripAction('update')) {
        showTripActionConfirmedSnackbar(intl, 'update', updatedViewTarget && (
          () => eventBus.dispatch({ type: 'view_itinerary_item', ...updatedViewTarget })
        ))
      }
      return
    }

    if (tripDatesChanged) {
      // Date changes have no View target, so they follow item and stop changes.
      if (shouldConfirmTripAction('date')) {
        showTripDatesConfirmedSnackbar(intl)
      }
    }

    // A delete confirmation has no View action and only wins when no add or update landed.
    const hasDeleted = normalizedUpdates.some(update => update.kind === 'stop_deleted'
      || update.kind === 'day_deleted'
      || update.kind === 'item_deleted')
    if (hasDeleted) {
      if (!tripDatesChanged && shouldConfirmTripAction('delete')) {
        showTripActionConfirmedSnackbar(intl, 'delete')
      }
      return
    }

    const firstUpdate = normalizedUpdates[0]
    const firstScrollTarget = firstUpdate ? resolveScrollTarget(firstUpdate) : undefined
    if (firstScrollTarget) {
      setScrollTarget(firstScrollTarget)
    }
  }, [queryClient, intl, currentRegionCode, eventBus, activeAssistantMessageIdRef])

  const handleEnrichmentComplete = useCallback((chatIdArg: number, event: EnrichmentCompleteEvent) => {
    // Optimistic cache patch — sets enrichmentStatus: 'enriched' on the entity so the
    // derived enriching Sets immediately exclude it. Also merges enrichment data (images, place detail).
    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
        (prev) => applyEnrichmentUpdate(prev, event),
      )
    } catch {
      // Enrichment update failed, will be corrected by server response
    }
  }, [queryClient, currentRegionCode])

  const handleRefetchItinerary = useCallback((chatIdArg: number) => {
    if (stopOnlyAddChatIdRef.current === chatIdArg) {
      eventBus.dispatch({ type: 'itinerary_stop_added' })
      stopOnlyAddChatIdRef.current = undefined
    }
    itemAddedChatIdRef.current = undefined
    queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatIdArg) })
  }, [eventBus, queryClient])

  // Wrapper for non-streaming refetch (user actions on current chat)
  const refetchCurrentChatItinerary = useCallback(() => {
    if (chatId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatId) })
    }
  }, [chatId, queryClient])

  return {
    itineraryData: orderedItineraryData,
    isItineraryLoading,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    addedItemLink,
    handleItineraryUpdates,
    handleEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  }
}
